import "./dispute.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomBreadcrumb } from "../../../shared/components/custom-breadcrumb/custom-breadcrumb";
import { CustomTable } from "../../../shared/components/custom-table/custom-table";
import { SERVICES } from "../../../core/services/services";
import { CustomLoader } from "../../../shared/components/custom-loader/custom-loader";
import { CustomModal } from "../../../shared/components/custom-modal/custom-modal";
import { Toast } from "primereact/toast";
import { DisputeDetails } from "./dispute-details";
import { DisputesFilter } from "./disputes-filter";
import { HELPER } from "../../../shared/helper/helper";
import ErrorBoundary from "../../../shared/components/error-boundary/error-boundary";
import { DisputeTypes, UserTypes } from "../../../shared/constants";
import { Optional } from "../../../shared/components/optional/optional";

export function BackofficeDisputes(props) {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [range] = useState(5);
  const [emptyText, setEmptyText] = useState("");
  const [mobile, setMobile] = useState(false);
  const [selectedDisputeLogCode, setSelectedDisputeLogCode] = useState(null);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [disputes, setDisputes] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [searchParams, setSearchParams] = useState(null);
  const [shouldHideBreadcrumb, setShouldHideBreadcrumb] = useState(false);

  const tableHeaders = [
    { label: "Payment Facilitator ID", value: "paymentFacilitatorId" },
    { label: "Log code", value: "logCode" },
    { label: "Account Number", value: "customerAccountNumber" },
    { label: "Mandate Reference", value: "mandateReference" },
    { label: "Transaction Status", value: "transactionStatus" },
    { label: "Dispute status", value: "status" },
    { label: "Resolution status", value: "resolutionStatus" },
    { label: "Transaction Date", value: "transactionTime" },
    { label: "Dispute Log Date", value: "loggedOn" },
    { label: "Actions", value: "actions" },
  ];

  const openAction = useCallback((e, action, isMobile) => {
    // eslint-disable-next-line default-case
    switch (action) {
      case "VIEW": {
        setSelectedDisputeLogCode(e?.logCode);
        setMobile(!!isMobile);
        setCurrentIndex(3);
        break;
      }
    }
  }, []);

  const getDisputes = useCallback(
    (query) => {
      setCurrentIndex(0);
      setIsModalVisible(false);
      setLoading(true);
      setDisputes([]);
      if (query) {
        setSearch(true);
      }
      let params = {
        page: pageNo,
        size: 10,
        ...(query && query),
        ...(searchParams && searchParams),
      };
      params = HELPER.TO_URL_STRING(params);
      const apiUrl = `/backoffice/dispute/${
        props.disputeType === DisputeTypes.Backoffice.PaymentFacilitatorDisputes
          ? "payment-facilitator"
          : "bank"
      }`;
      SERVICES.MAKE_GET_REQUEST(apiUrl, params)
        // SERVICES.GET_DISPUTE(params)
        .then((data) => {
          const result = data.result.content;
          if (!result.length) {
            setEmptyText("No disputes found...");
          } else {
            let arr = [];
            setTotalItems(data?.result?.totalElements);
            setTotalPages(data?.result?.totalPages);
            result.forEach((e) => {
              arr.push({
                ...e,
                actions: "CR",
                detailsFunction: openAction,
              });
            });
            setDisputes(arr);
          }
          setError(null);
          setCurrentIndex(1);
          setLoading(false);
        })
        .catch((error) => {
          setError(HELPER.PROCESS_ERROR(error));
          setCurrentIndex(1);
          setLoading(false);
        });
    },
    [pageNo, openAction, props.disputeType, searchParams]
  );

  useEffect(() => {
    getDisputes();
  }, [getDisputes]);

  function goToPage(pageNo) {
    setPageNo(pageNo);
  }

  function openModal(index, isMobile) {
    setCurrentModalIndex(index);
    if (isMobile) {
      setCurrentIndex(2);
    } else {
      setIsModalVisible(true);
    }
  }

  function reset() {
    setPageNo(0);
    setSearchParams(null);
    setLoading(true);
    setSearch(false);
    setCurrentIndex(0);
    getDisputes();
  }

  function reload() {
    setLoading(true);
    setCurrentIndex(0);
    if (searchParams) {
      getDisputes(searchParams);
    } else {
      getDisputes();
    }
  }

  function closeModal(shouldReload) {
    if (isModalVisible) {
      setIsModalVisible(false);
    }
    if (currentIndex === 2) {
      setCurrentIndex(1);
    }
    if (shouldReload === true) {
      reload();
    }
  }

  function saveSearchParams(searchParams) {
    setPageNo(0);
    setSearchParams(searchParams);
    setIsModalVisible(false);
    getDisputes(searchParams);
  }

  const mobileModal = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 1:
        return (
          <DisputesFilter
            searchFunction={saveSearchParams}
            closeModal={closeModal}
            disputeType={props.disputeType}
            failedResponseDisputeCodes={props.failedResponseDisputeCodes}
          />
        );
    }
  };

  const modalContent = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 1:
        return (
          <DisputesFilter
            searchFunction={saveSearchParams}
            closeModal={closeModal}
            disputeType={props.disputeType}
            failedResponseDisputeCodes={props.failedResponseDisputeCodes}
          />
        );
    }
  };

  function onHide() {}

  function goBack(refresh) {
    if (refresh === true) {
      reload();
      return;
    }
    setCurrentIndex(1);
  }

  const disputeView = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return (
          <div className="loading-container">
            <CustomLoader loadingText="Loading disputes..." />
          </div>
        );
      case 1:
        return (
          <CustomTable
            isReload={true}
            totalPages={totalPages}
            totalItems={totalItems}
            currentPage={pageNo + 1}
            range={range}
            emptyText={emptyText}
            search={search || searchParams}
            reload={reset}
            error={error}
            items={disputes}
            headers={tableHeaders}
            nextPage={() => setPageNo(pageNo + 1)}
            prevPage={() => setPageNo(pageNo - 1)}
            goToFirstPage={() => setPageNo(0)}
            goToLastPage={() => setPageNo(totalPages - 1)}
            goToPage={goToPage}
            hasColumnWithCustomWidth={true}
            customColumnWidth={15}
            customWidthColumnName="logCode"
          />
        );
      case 2:
        return (
          <div className="mobile-modal-container">
            <div className="custom-card">
              <div className="mobile-card-position">{mobileModal()}</div>
            </div>
          </div>
        );
      case 3:
        return (
          <DisputeDetails
            logCode={selectedDisputeLogCode}
            goBack={goBack}
            mobile={mobile}
            userType={UserTypes.Backoffice}
            toggleBreadCrumbView={() =>
              setShouldHideBreadcrumb(!shouldHideBreadcrumb)
            }
          />
        );
    }
  };

  return (
    <ErrorBoundary>
      <div className="disputes">
        <Toast ref={toast} />
        <div>
          <CustomModal
            closeModal={closeModal}
            onHide={onHide}
            visible={isModalVisible}
            modalContent={modalContent}
          />
        </div>
        <Optional show={currentIndex !== 3 && !shouldHideBreadcrumb}>
          <div className="page-title p-text-left">Disputes</div>
          <div className="p-mt-2">
            <CustomBreadcrumb page={`Manage Disputes`} />
          </div>
        </Optional>
        <Optional show={currentIndex === 1}>
          <div className="p-grid p-mt-2 p-mb-5">
            <div className="p-col-10"></div>
            <div className="p-col-2">
              <button
                disabled={loading}
                onClick={() => openModal(1, false)}
                className="primary-button"
              >
                <i className="pi pi-filter" />
                <span className="hide-btn-text"> Filter</span>
              </button>
            </div>
          </div>
        </Optional>
        <div>{disputeView()}</div>
      </div>
    </ErrorBoundary>
  );
}
